import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useMode } from '@/providers/ModeProvider';
import { dataService, isDemoMode } from '@/services/data-service';
import type { Asset, AssetStatus, AssetCategory } from '@/types/asset';
import { AssetGallery } from '@/components/assets/asset-gallery';
import { AssetEditor } from '@/components/assets/asset-editor';
import { AssetFilters } from '@/components/assets/asset-filters';
import { DisposalWizard } from '@/components/assets/disposal-wizard';
import { LabelPrintingModal } from '@/components/assets/label-printing-modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Search, LayoutGrid, List, ArrowUpDown, Grid3x3, Trash2, Share2, Download, FileSpreadsheet, Mail, TrendingDown, History, MoreVertical, TrendingUp, DollarSign, Settings2, X, FileText, Printer, Package, Filter } from 'lucide-react';
import { exportToCSV, exportToExcel, generateFilename, formatDateForExport, formatCurrencyForExport } from '@/lib/export-utils';
import type { ExportColumn } from '@/lib/export-utils';
import { toast } from 'sonner';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

type SortOption = 'category-desc' | 'category-asc' | 'name-asc' | 'name-desc' | 'date-desc' | 'date-asc' | 'value-desc' | 'value-asc';
type GridSize = 4 | 5 | 6;

export function AssetsPage(): React.ReactElement {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { mode } = useMode();
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [editorMode, setEditorMode] = useState<'create' | 'edit' | 'view'>('edit');
  const [selectedAssetIds, setSelectedAssetIds] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<'gallery' | 'list'>('gallery');
  const [gridSize, setGridSize] = useState<GridSize>(5);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('category-asc');
  const [filters, setFilters] = useState<{
    status?: AssetStatus[];
    category?: AssetCategory[];
    location?: string[];
  }>({
    status: ['active'], // Default to showing only active assets
  });
  const [importJobInfo, setImportJobInfo] = useState<{ jobId: string; fileName: string } | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [disposalAsset, setDisposalAsset] = useState<Asset | null>(null);
  const [showLabelPrintingModal, setShowLabelPrintingModal] = useState(false);

  useEffect(() => {
    const statusParam = searchParams.get('status');
    const locationParam = searchParams.get('location');
    const categoryParam = searchParams.get('category');
    const idParam = searchParams.get('id');
    const filterParam = searchParams.get('filter');
    const importJobIdParam = searchParams.get('importJobId');
    
    if (statusParam || locationParam || categoryParam) {
      setFilters((prev) => ({
        ...prev,
        ...(statusParam && { status: [statusParam as AssetStatus] }),
        ...(locationParam && { location: [decodeURIComponent(locationParam)] }),
        ...(categoryParam && { category: [decodeURIComponent(categoryParam) as AssetCategory] }),
      }));
    }

    // Handle "requiring-attention" filter from Insights page
    if (filterParam === 'requiring-attention') {
      // This will be handled in the filteredAssets logic below
      // We don't set filters here because it's a complex OR condition
    }

    // Handle import job ID filter
    if (importJobIdParam) {
      const fileNameParam = searchParams.get('fileName');
      setImportJobInfo({
        jobId: importJobIdParam,
        fileName: fileNameParam || 'Unknown Import'
      });
    } else {
      setImportJobInfo(null);
    }

    // Handle direct asset navigation via id parameter
    // Only open the modal if there's an id param AND no asset is currently selected
    // This prevents reopening the modal after a save operation
    if (idParam && assets.length > 0 && !selectedAsset) {
      const asset = assets.find((a) => a.id === idParam);
      if (asset) {
        setSelectedAsset(asset);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    async function loadAssets() {
      try {
        setLoading(true);
        setError(null);
        // Request all assets with a large page size to ensure we get everything
        const data = await dataService.getAssets({ pageSize: 10000 });
        const assetList = Array.isArray(data) ? data : (data as any)?.data || [];
        setAssets(assetList);
      } catch (err) {
        console.error('Failed to load assets:', err);
        setError('Failed to load assets. Please try again.');
      } finally {
        setLoading(false);
      }
    }
    loadAssets();
  }, [mode]);

  const filteredAssets = assets
    .filter((asset) => {
      // Handle import job ID filter
      if (importJobInfo) {
        if (asset.importJobId !== importJobInfo.jobId) return false;
      }

      // Handle "requiring-attention" filter (OR logic for multiple conditions)
      const filterParam = searchParams.get('filter');
      if (filterParam === 'requiring-attention') {
        const today = new Date();
        const thirtyDaysFromNow = new Date(today);
        thirtyDaysFromNow.setDate(today.getDate() + 30);

        // Check if warranty expires within 30 days
        const warrantyExpiring = asset.warrantyExpiryDate && 
          new Date(asset.warrantyExpiryDate) <= thirtyDaysFromNow &&
          new Date(asset.warrantyExpiryDate) >= today;

        // Check if insurance expires within 30 days
        const insuranceExpiring = asset.insuranceExpiryDate && 
          new Date(asset.insuranceExpiryDate) <= thirtyDaysFromNow &&
          new Date(asset.insuranceExpiryDate) >= today;

        // Check if maintenance due within 30 days (using userDate1 as maintenance due date)
        const maintenanceDue = asset.userDate1 && 
          new Date(asset.userDate1) <= thirtyDaysFromNow &&
          new Date(asset.userDate1) >= today;

        // Check if asset status indicates upcoming disposal
        const upcomingDisposal = asset.status === 'disposed';

        // Return true if ANY condition matches (OR logic)
        if (warrantyExpiring || insuranceExpiring || maintenanceDue || upcomingDisposal) {
          return true;
        }
        
        // If no conditions match, filter out this asset
        return false;
      }

      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch =
          asset.name.toLowerCase().includes(query) ||
          asset.assetId.toLowerCase().includes(query) ||
          asset.serialNumber?.toLowerCase().includes(query) ||
          asset.description?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }

      // Status filter
      if (filters.status && filters.status.length > 0) {
        if (!filters.status.includes(asset.status)) return false;
      }

      // Category filter
      if (filters.category && filters.category.length > 0) {
        if (!filters.category.includes(asset.category)) return false;
      }

      // Location filter
      if (filters.location && filters.location.length > 0) {
        if (!filters.location.includes(asset.location)) return false;
      }

      return true;
    })
    .sort((a: any, b: any) => {
      switch (sortBy) {
        case 'category-desc':
          return b.category.localeCompare(a.category);
        case 'category-asc':
          return a.category.localeCompare(b.category);
        case 'name-asc':
          return a.name.localeCompare(b.name);
        case 'name-desc':
          return b.name.localeCompare(a.name);
        case 'date-desc':
          return new Date(b.acquisitionDate).getTime() - new Date(a.acquisitionDate).getTime();
        case 'date-asc':
          return new Date(a.acquisitionDate).getTime() - new Date(b.acquisitionDate).getTime();
        case 'value-desc':
          return b.netBookValue - a.netBookValue;
        case 'value-asc':
          return a.netBookValue - b.netBookValue;
        default:
          return 0;
      }
    });

  const handleAssetClick = (asset: Asset, mode?: 'edit' | 'view') => {
    // Open asset in modal
    setSelectedAsset(asset);
    setEditorMode(mode || 'edit');
    // Update URL with asset id for deep linking
    navigate(`/assets?id=${asset.id}`, { replace: true });
  };

  const handleAssetSave = async (updatedAsset: Asset) => {
    try {
      if (updatedAsset.id.startsWith('new-')) {
        if (isDemoMode()) {
          setAssets((prev) => [...prev, { ...updatedAsset, id: `demo-${Date.now()}` }]);
        } else {
          const created = await dataService.createAsset(updatedAsset);
          setAssets((prev) => [...prev, created]);
        }
      } else {
        if (isDemoMode()) {
          setAssets((prev) =>
            prev.map((a) => (a.id === updatedAsset.id ? updatedAsset : a))
          );
        } else {
          const updated = await dataService.updateAsset(updatedAsset.id, updatedAsset);
          setAssets((prev) =>
            prev.map((a) => (a.id === updated.id ? updated : a))
          );
        }
      }
      toast.success('Asset saved successfully');
      // Close the modal and clear URL after successful save
      setSelectedAsset(null);
      navigate('/assets', { replace: true });
    } catch (err: any) {
      console.error('Failed to save asset:', err);
      const errorMessage = err?.message || 'Failed to save asset. Please try again.';
      toast.error(errorMessage);
    }
  };

  const handleAssetClose = () => {
    setSelectedAsset(null);
    // Clear the id parameter from URL
    navigate('/assets', { replace: true });
  };

  const handleNewAsset = () => {
    const newAsset: Asset = {
      id: `new-${Date.now()}`,
      assetId: '',
      name: '',
      category: 'Computer Equipment',
      location: '',
      status: 'active',
      acquisitionCost: 0,
      netBookValue: 0,
      accumulatedDepreciation: 0,
      currency: 'GBP',
      acquisitionDate: new Date().toISOString().split('T')[0],
      createdBy: 'Current User',
      createdDate: new Date().toISOString(),
    };
    setSelectedAsset(newAsset);
    setEditorMode('create');
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedAssetIds(new Set(filteredAssets.map((a) => a.id)));
    } else {
      setSelectedAssetIds(new Set());
    }
  };

  const handleSelectAsset = (assetId: string, checked: boolean) => {
    const newSelection = new Set(selectedAssetIds);
    if (checked) {
      newSelection.add(assetId);
    } else {
      newSelection.delete(assetId);
    }
    setSelectedAssetIds(newSelection);
  };

  const handleDelete = () => {
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      // If in demo mode, just update local state
      if (isDemoMode()) {
        setAssets((prev) => prev.filter((a) => !selectedAssetIds.has(a.id)));
        setSelectedAssetIds(new Set());
        setShowDeleteDialog(false);
        toast.success(`Deleted ${selectedAssetIds.size} asset(s)`);
        return;
      }

      // In API mode, delete from backend
      const deletePromises = Array.from(selectedAssetIds).map(id => 
        dataService.deleteAsset(id)
      );

      await Promise.all(deletePromises);

      // Update local state after successful deletion
      setAssets((prev) => prev.filter((a) => !selectedAssetIds.has(a.id)));
      setSelectedAssetIds(new Set());
      setShowDeleteDialog(false);
      toast.success(`Successfully deleted ${selectedAssetIds.size} asset(s)`);
    } catch (error: any) {
      console.error('Failed to delete assets:', error);
      setShowDeleteDialog(false);
      
      // Try to extract error message from API response
      let errorMessage = 'Failed to delete assets. Please try again.';
      
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    }
  };

  const handleExportCSV = () => {
    try {
      // Export filtered data (respecting current view)
      const dataToExport = viewMode === 'gallery' ? filteredAssets : filteredAssets;
      
      // Define columns based on list/table view (as per requirements)
      const columns: ExportColumn[] = [
        { key: 'assetId', label: 'Asset ID' },
        { key: 'name', label: 'Asset Name' },
        { key: 'description', label: 'Description' },
        { key: 'category', label: 'Category' },
        { key: 'location', label: 'Location' },
        { key: 'status', label: 'Status' },
        { key: 'serialNumber', label: 'Serial Number' },
        { key: 'manufacturer', label: 'Manufacturer' },
        { key: 'model', label: 'Model' },
        { key: 'acquisitionDate', label: 'Acquisition Date', format: formatDateForExport },
        { key: 'acquisitionCost', label: 'Acquisition Cost', format: formatCurrencyForExport },
        { key: 'netBookValue', label: 'Net Book Value', format: formatCurrencyForExport },
        { key: 'accumulatedDepreciation', label: 'Accumulated Depreciation', format: formatCurrencyForExport },
        { key: 'assignedTo', label: 'Assigned To' },
        { key: 'department', label: 'Department' },
        { key: 'warrantyExpiryDate', label: 'Warranty Expiry', format: formatDateForExport },
        { key: 'insuranceExpiryDate', label: 'Insurance Expiry', format: formatDateForExport },
      ];

      exportToCSV({
        filename: generateFilename('assets-export', 'csv'),
        columns,
        data: dataToExport,
      });

      toast.success(`Exported ${dataToExport.length} asset(s) to CSV`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export data');
    }
  };

  const handleExportExcel = () => {
    try {
      // Export filtered data (respecting current view)
      const dataToExport = viewMode === 'gallery' ? filteredAssets : filteredAssets;
      
      // Define columns based on list/table view (as per requirements)
      const columns: ExportColumn[] = [
        { key: 'assetId', label: 'Asset ID' },
        { key: 'name', label: 'Asset Name' },
        { key: 'description', label: 'Description' },
        { key: 'category', label: 'Category' },
        { key: 'location', label: 'Location' },
        { key: 'status', label: 'Status' },
        { key: 'serialNumber', label: 'Serial Number' },
        { key: 'manufacturer', label: 'Manufacturer' },
        { key: 'model', label: 'Model' },
        { key: 'acquisitionDate', label: 'Acquisition Date', format: formatDateForExport },
        { key: 'acquisitionCost', label: 'Acquisition Cost', format: formatCurrencyForExport },
        { key: 'netBookValue', label: 'Net Book Value', format: formatCurrencyForExport },
        { key: 'accumulatedDepreciation', label: 'Accumulated Depreciation', format: formatCurrencyForExport },
        { key: 'assignedTo', label: 'Assigned To' },
        { key: 'department', label: 'Department' },
        { key: 'warrantyExpiryDate', label: 'Warranty Expiry', format: formatDateForExport },
        { key: 'insuranceExpiryDate', label: 'Insurance Expiry', format: formatDateForExport },
      ];

      exportToExcel({
        filename: generateFilename('assets-export', 'xls'),
        sheetName: 'Assets',
        columns,
        data: dataToExport,
      });

      toast.success(`Exported ${dataToExport.length} asset(s) to Excel`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export data');
    }
  };

  const handleEmailExport = () => {
    const selectedAssets = assets.filter((a) => selectedAssetIds.has(a.id));
    const body = selectedAssets.map((a) => 
      `${a.assetId} - ${a.name} (${a.category}) - ${a.currency} ${a.netBookValue.toLocaleString()}`
    ).join('\n');
    
    window.location.href = `mailto:?subject=Asset Export&body=${encodeURIComponent(body)}`;
  };

  const handleCreateDepreciation = () => {
    const selectedAssets = assets.filter((a) => selectedAssetIds.has(a.id));
    // Navigate to depreciation page with selected assets
    navigate('/transactions/depreciation', { state: { selectedAssets } });
  };

  const handleViewHistory = () => {
    const assetIds = Array.from(selectedAssetIds);
    // Navigate to enquiry page with asset filter
    navigate(`/enquiry?assetIds=${assetIds.join(',')}`);
  };

  const handleViewAudit = () => {
    const assetIds = Array.from(selectedAssetIds);
    // Navigate to audit page with asset filter
    navigate(`/audit?assetId=${assetIds.join(',')}`);
  };

  const handleAddition = () => {
    const selectedAssets = assets.filter((a) => selectedAssetIds.has(a.id));
    // Navigate to additions page with selected assets
    navigate('/transactions/additions', { state: { selectedAssets } });
  };

  const handleRevaluation = () => {
    const selectedAssets = assets.filter((a) => selectedAssetIds.has(a.id));
    // Navigate to revaluation page with selected assets
    navigate('/transactions/revaluation', { state: { selectedAssets } });
  };

  const handleDisposal = () => {
    // Only allow disposal for a single asset
    if (selectedAssetIds.size === 1) {
      const assetId = Array.from(selectedAssetIds)[0];
      const asset = assets.find((a) => a.id === assetId);
      if (asset) {
        // Check if asset has zero quantity
        if (asset.quantity !== undefined && asset.quantity <= 0) {
          toast.error('No quantity available to dispose');
          return;
        }
        setDisposalAsset(asset);
      }
    }
  };

  const handleDisposalComplete = () => {
    // Reload assets after disposal
    async function reloadAssets() {
      try {
        const data = await dataService.getAssets({ pageSize: 10000 });
        const assetList = Array.isArray(data) ? data : (data as any)?.data || [];
        setAssets(assetList);
        setSelectedAssetIds(new Set());
      } catch (err) {
        console.error('Failed to reload assets:', err);
      }
    }
    reloadAssets();
  };

  const handlePrintLabels = () => {
    setShowLabelPrintingModal(true);
  };

  const selectedAssets = assets.filter((a) => selectedAssetIds.has(a.id));
  const totalValue = selectedAssets.reduce((sum: any, asset: any) => sum + asset.netBookValue, 0);
  const currency = selectedAssets.length > 0 ? selectedAssets[0].currency : 'GBP';

  return (
    <div className="flex h-full flex-col">
      {/* Toolbar */}
      <div className="border-b bg-card p-4">
        <div className="flex items-center gap-3">
          {/* Search Bar */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search assets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Vertical Divider */}
          <div className="h-8 w-px bg-border" />

          {/* Filters */}
          <AssetFilters filters={filters} onFiltersChange={setFilters} />

          {/* Sorting */}
          <Select value={sortBy} onValueChange={(value: string) => setSortBy(value as SortOption)}>
            <SelectTrigger className="w-[180px]">
              <ArrowUpDown className="mr-2 h-4 w-4" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="category-asc">Category (A-Z)</SelectItem>
              <SelectItem value="category-desc">Category (Z-A)</SelectItem>
              <SelectItem value="name-asc">Name (A-Z)</SelectItem>
              <SelectItem value="name-desc">Name (Z-A)</SelectItem>
              <SelectItem value="date-desc">Date (Newest)</SelectItem>
              <SelectItem value="date-asc">Date (Oldest)</SelectItem>
              <SelectItem value="value-desc">Value (High-Low)</SelectItem>
              <SelectItem value="value-asc">Value (Low-High)</SelectItem>
            </SelectContent>
          </Select>

          {/* Vertical Divider */}
          <div className="h-8 w-px bg-border" />

          {/* View Mode Toggle */}
          <ToggleGroup
            type="single"
            value={viewMode}
            onValueChange={(value) => value && setViewMode(value as 'gallery' | 'list')}
          >
            <ToggleGroupItem value="list" aria-label="List view">
              <List className="h-4 w-4" />
            </ToggleGroupItem>
            <ToggleGroupItem value="gallery" aria-label="Gallery view">
              <LayoutGrid className="h-4 w-4" />
            </ToggleGroupItem>
          </ToggleGroup>

          {/* Grid Size Selector */}
          {viewMode === 'gallery' && (
            <Select value={gridSize.toString()} onValueChange={(value: string) => setGridSize(Number(value) as GridSize)}>
              <SelectTrigger className="w-[140px]">
                <Grid3x3 className="mr-2 h-4 w-4" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="4">4 Columns</SelectItem>
                <SelectItem value="5">5 Columns</SelectItem>
                <SelectItem value="6">6 Columns</SelectItem>
              </SelectContent>
            </Select>
          )}

          {/* Spacer to push New Asset to the right */}
          <div className="flex-1" />

          {/* New Asset Button - Pinned to the right */}
          <Button onClick={handleNewAsset}>
            <Plus className="mr-2 h-4 w-4" />
            New Asset
          </Button>
        </div>
      </div>

      {/* Active Filter Badge */}
      {searchParams.get('filter') === 'requiring-attention' && (
        <div className="border-b bg-muted/50 px-4 py-2">
          <Badge variant="secondary" className="gap-2">
            <span>Assets Requiring Attention: Warranty, Insurance, or Maintenance expiring within 30 days, or Disposal status</span>
            <Button
              variant="ghost"
              size="sm"
              className="h-4 w-4 p-0 hover:bg-transparent"
              onClick={() => window.location.href = '/assets'}
            >
              <X className="h-3 w-3" />
            </Button>
          </Badge>
        </div>
      )}

      {/* Import Job Filter Badge */}
      {importJobInfo && (
        <div className="border-b bg-muted/50 px-4 py-2">
          <Badge variant="secondary" className="gap-2">
            <span>Showing assets from import: {importJobInfo.fileName}</span>
            <Button
              variant="ghost"
              size="sm"
              className="h-4 w-4 p-0 hover:bg-transparent"
              onClick={() => window.location.href = '/assets'}
            >
              <X className="h-3 w-3" />
            </Button>
          </Badge>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-auto bg-background">
        {loading ? (
          <div className="flex h-full items-center justify-center">
            <div className="text-center">
              <p className="text-lg font-medium">Loading assets...</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex h-full items-center justify-center">
            <div className="text-center">
              <p className="text-lg font-medium text-destructive">Error</p>
              <p className="text-sm text-muted-foreground">{error}</p>
            </div>
          </div>
        ) : filteredAssets.length === 0 ? (
          <div className="flex h-full items-center justify-center">
            <div className="text-center">
              <p className="text-lg font-medium">No assets found</p>
              <p className="text-sm text-muted-foreground">
                Try adjusting your search or filters
              </p>
            </div>
          </div>
        ) : (
          <div className="relative">
            {/* Sticky Select All, Count, and Action Buttons - Always visible */}
            <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-sm border-b px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Checkbox
                    checked={selectedAssetIds.size === filteredAssets.length && filteredAssets.length > 0}
                    indeterminate={selectedAssetIds.size > 0 && selectedAssetIds.size < filteredAssets.length}
                    onCheckedChange={handleSelectAll}
                    aria-label="Select all assets"
                  />
                  <span className="text-sm font-medium">
                    {selectedAssetIds.size > 0 
                      ? `${selectedAssetIds.size} item${selectedAssetIds.size !== 1 ? 's' : ''} selected`
                      : `${filteredAssets.length} asset${filteredAssets.length !== 1 ? 's' : ''}`
                    }
                  </span>
                </div>

                {/* Action Buttons - Only show when items are selected */}
                {selectedAssetIds.size > 0 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="sm">
                        <Settings2 className="mr-2 h-4 w-4" />
                        Actions
                      </Button>
                    </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    {/* Transactions Section */}
                    <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                      Transactions
                    </div>
                    <DropdownMenuItem onClick={handleAddition} disabled={selectedAssetIds.size === 0}>
                      <Plus className="mr-2 h-4 w-4" />
                      Addition
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleCreateDepreciation} disabled={selectedAssetIds.size === 0}>
                      <TrendingDown className="mr-2 h-4 w-4" />
                      Depreciation
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleRevaluation} disabled={selectedAssetIds.size === 0}>
                      <TrendingDown className="mr-2 h-4 w-4" />
                      Revaluation
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={handleDisposal}
                      disabled={selectedAssetIds.size !== 1}
                      title={selectedAssetIds.size !== 1 ? 'Select a single asset to dispose' : ''}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Disposal
                    </DropdownMenuItem>
                    
                    <DropdownMenuSeparator />
                    
                    {/* View Transaction History */}
                    <DropdownMenuItem onClick={handleViewHistory} disabled={selectedAssetIds.size === 0}>
                      <History className="mr-2 h-4 w-4" />
                      View Transaction History
                    </DropdownMenuItem>
                    
                    {/* View Audit */}
                    <DropdownMenuItem onClick={handleViewAudit} disabled={selectedAssetIds.size === 0}>
                      <FileText className="mr-2 h-4 w-4" />
                      View Audit
                    </DropdownMenuItem>
                    
                    <DropdownMenuSeparator />
                    
                    {/* Actions Section */}
                    <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                      Actions
                    </div>
                    <DropdownMenuItem onClick={handlePrintLabels} disabled={selectedAssetIds.size === 0}>
                      <Printer className="mr-2 h-4 w-4" />
                      Print Asset Labels
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleDelete} disabled={selectedAssetIds.size === 0} className="text-destructive focus:text-destructive">
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                )}
              </div>
            </div>

            {/* Asset Gallery */}
            <div className="px-6 py-4">
              <AssetGallery
                assets={filteredAssets}
                viewMode={viewMode}
                gridSize={gridSize}
                onAssetClick={handleAssetClick}
                selectedAssetIds={selectedAssetIds}
                onSelectAsset={handleSelectAsset}
              />
            </div>
          </div>
        )}
      </div>

      {/* Asset Editor Dialog */}
      {selectedAsset && (
        <AssetEditor
          asset={selectedAsset}
          open={!!selectedAsset}
          onClose={handleAssetClose}
          onSave={handleAssetSave}
          mode={editorMode}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete {selectedAssetIds.size} asset(s). This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Disposal Wizard */}
      {disposalAsset && (
        <DisposalWizard
          asset={disposalAsset}
          open={!!disposalAsset}
          onClose={() => setDisposalAsset(null)}
          onComplete={handleDisposalComplete}
        />
      )}

      {/* Label Printing Modal */}
      <LabelPrintingModal
        open={showLabelPrintingModal}
        onClose={() => setShowLabelPrintingModal(false)}
        assets={selectedAssets}
      />
    </div>
  );
}

export default AssetsPage;
